// Tiny dependency-free YAML frontmatter parser tuned to the build-doc schema.
// Phase E of the 4-layer architecture migration adds YAML frontmatter to every
// logic doc; Phase F surfaces those fields in the HelpAnchor drawer. The
// schema is fixed and shallow — a handful of scalars, two lists, one
// list-of-objects — so we avoid adding `gray-matter` / `js-yaml` for this.
//
// Recognised shape (canonical example, all fields optional):
//
//   ---
//   surface: chapter-workbench
//   module: Reporting / Writing
//   schema:
//     - Chapter
//     - ChapterSection
//     - ConflictReference
//   engine: Writer Engine
//   consumes:
//     - engine: Quality Engine
//       handshake: H6
//   transformation: src/versions/v2/modules/report/ChapterWorkbench.transform.ts
//   ---
//
// Frontmatter must be at the very top of the file and delimited by `---`
// lines. Anything not matching that exact opening is treated as
// frontmatter-absent and the full source returned as the body.

export type DocConsumes = { engine: string; handshake: string };

export type DocFrontmatter = {
  surface?: string;
  module?: string;
  schema?: string[];
  engine?: string;
  consumes?: DocConsumes[];
  transformation?: string;
};

export type ParsedDoc = {
  fm: DocFrontmatter | null;
  body: string;
};

function stripQuotes(value: string): string {
  const trimmed = value.trim();
  if (trimmed.length >= 2) {
    const first = trimmed.charAt(0);
    const last = trimmed.charAt(trimmed.length - 1);
    if ((first === '"' && last === '"') || (first === "'" && last === "'")) {
      return trimmed.slice(1, -1);
    }
  }
  return trimmed;
}

// Detect indentation depth (count of leading spaces) on a non-empty line.
function indentOf(line: string): number {
  let n = 0;
  while (n < line.length && line.charCodeAt(n) === 32 /* space */) n += 1;
  return n;
}

// Parse a `- key: value` / nested-`key: value` block of objects into a list of
// {engine, handshake}-shaped records. Tolerant of either single-line
// (`- engine: X` followed by `  handshake: Y`) or compact-on-one-line
// (`- engine: X, handshake: Y` — not generated by our schema but harmless).
function parseConsumesList(lines: string[]): DocConsumes[] {
  const out: DocConsumes[] = [];
  let current: Partial<DocConsumes> | null = null;
  for (const rawLine of lines) {
    const line = rawLine.replace(/\s+$/, '');
    if (line.trim() === '') continue;
    if (/^\s*-\s/.test(line)) {
      // New record starts. Push previous if complete.
      if (current && current.engine && current.handshake) {
        out.push({ engine: current.engine, handshake: current.handshake });
      }
      current = {};
      // Strip the `- ` prefix and treat the rest as the first k:v of the new
      // record (e.g. `- engine: Quality Engine`).
      const rest = line.replace(/^\s*-\s+/, '');
      const match = rest.match(/^([A-Za-z_][A-Za-z0-9_]*)\s*:\s*(.*)$/);
      if (match) {
        const key = match[1];
        const value = stripQuotes(match[2]);
        if (key === 'engine' || key === 'handshake') {
          current[key] = value;
        }
      }
    } else {
      // Continuation k:v belonging to the current record.
      const match = line.match(/^\s+([A-Za-z_][A-Za-z0-9_]*)\s*:\s*(.*)$/);
      if (match && current) {
        const key = match[1];
        const value = stripQuotes(match[2]);
        if (key === 'engine' || key === 'handshake') {
          current[key] = value;
        }
      }
    }
  }
  if (current && current.engine && current.handshake) {
    out.push({ engine: current.engine, handshake: current.handshake });
  }
  return out;
}

// Parse a `- item` / `- item` block into a string list.
function parseStringList(lines: string[]): string[] {
  const out: string[] = [];
  for (const rawLine of lines) {
    const line = rawLine.replace(/\s+$/, '');
    if (line.trim() === '') continue;
    const match = line.match(/^\s*-\s+(.*)$/);
    if (match) {
      const value = stripQuotes(match[1]);
      if (value) out.push(value);
    }
  }
  return out;
}

// Group raw frontmatter lines under their top-level key. Returns a map of
// key → list of indented child lines (children may be empty if the value is
// scalar on the same line as the key).
function groupByKey(lines: string[]): Record<string, { scalar: string | null; children: string[] }> {
  const groups: Record<string, { scalar: string | null; children: string[] }> = {};
  let currentKey: string | null = null;
  let currentIndent = 0;
  for (const rawLine of lines) {
    const line = rawLine.replace(/\s+$/, '');
    if (line.trim() === '') continue;
    const indent = indentOf(line);
    const isTopLevel = indent === 0;
    if (isTopLevel) {
      const match = line.match(/^([A-Za-z_][A-Za-z0-9_]*)\s*:\s*(.*)$/);
      if (match) {
        const key = match[1];
        const rest = match[2];
        groups[key] = { scalar: rest.length > 0 ? stripQuotes(rest) : null, children: [] };
        currentKey = key;
        currentIndent = indent;
      } else {
        currentKey = null;
      }
    } else if (currentKey && indent > currentIndent) {
      groups[currentKey].children.push(line);
    }
  }
  return groups;
}

// Public API — split a raw markdown source into optional frontmatter + body.
// If no frontmatter is present, `fm` is `null` and `body` is the original
// source unchanged.
export function parseFrontmatter(raw: string): ParsedDoc {
  if (!raw.startsWith('---')) {
    return { fm: null, body: raw };
  }
  // Allow either `\n` or `\r\n` line endings.
  const lines = raw.split(/\r?\n/);
  if (lines[0].trim() !== '---') {
    return { fm: null, body: raw };
  }
  let closingIdx = -1;
  for (let i = 1; i < lines.length; i += 1) {
    if (lines[i].trim() === '---') {
      closingIdx = i;
      break;
    }
  }
  if (closingIdx === -1) {
    // Malformed — no closing `---`. Treat as no frontmatter.
    return { fm: null, body: raw };
  }
  const fmLines = lines.slice(1, closingIdx);
  const bodyLines = lines.slice(closingIdx + 1);
  // Drop a single leading blank line in the body, since the editor convention
  // is `---\n\n# Heading` (blank line after closing delimiter).
  if (bodyLines.length > 0 && bodyLines[0].trim() === '') {
    bodyLines.shift();
  }
  const body = bodyLines.join('\n');

  const groups = groupByKey(fmLines);
  const fm: DocFrontmatter = {};

  if (groups.surface?.scalar) fm.surface = groups.surface.scalar;
  if (groups.module?.scalar) fm.module = groups.module.scalar;
  if (groups.engine?.scalar) fm.engine = groups.engine.scalar;
  if (groups.transformation?.scalar) fm.transformation = groups.transformation.scalar;

  if (groups.schema) {
    const list = parseStringList(groups.schema.children);
    if (list.length > 0) fm.schema = list;
  }
  if (groups.consumes) {
    const list = parseConsumesList(groups.consumes.children);
    if (list.length > 0) fm.consumes = list;
  }

  // If nothing matched, treat as no frontmatter so the metadata panel stays
  // hidden rather than rendering an empty header above the body.
  const hasAny =
    fm.surface ||
    fm.module ||
    fm.engine ||
    fm.transformation ||
    (fm.schema && fm.schema.length > 0) ||
    (fm.consumes && fm.consumes.length > 0);

  return { fm: hasAny ? fm : null, body };
}
